import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { getReceiptReport } from '../../../../redux/actions/SocietyPortal/InvoiceReportAction/InvoiceReportAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { exportReceiptReportReq } from '../../../../axios/request/SocietyPortal/InvoiceReportReqs/InvoiceReportReqs';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { PaginationLimit } from '../../../../components/PaginationLimit';

const PAYMENT_MODES = ['ALL', 'CASH', 'CHEQUE', 'NEFT', 'RTGS', 'UPI', 'CARD', 'DD', 'OTHER'];
const STATUS_OPTIONS = [
    { label: 'All', value: 'ALL' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Cleared', value: 'CLEARED' },
    { label: 'Bounced', value: 'BOUNCED' },
    { label: 'Cancelled', value: 'CANCELLED' },
];
const TYPE_OPTIONS = [
    { label: 'All', value: 'ALL' },
    { label: 'Payment', value: 'PAYMENT' },
    { label: 'Advance', value: 'ADVANCE' },
];

const STATUS_STYLES = {
    CLEARED: 'tw-bg-emerald-50 tw-text-emerald-700',
    PENDING: 'tw-bg-yellow-50 tw-text-yellow-700',
    BOUNCED: 'tw-bg-red-50 tw-text-red-700',
    CANCELLED: 'tw-bg-gray-100 tw-text-gray-500',
};

const ReceiptReport = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { receiptReportData, receiptReportSummary, receiptReportPagination, receiptReportLoading } = useSelector(state => state.InvoiceReportReducer || {});
    const { wingsDropdown, flatsDropdown } = useSelector(state => state.WingsAndFlatsReducers || {});

    const [filters, setFilters] = useState({
        wing_id: '', flat_id: '', from_date: '', to_date: '',
        payment_mode: 'ALL', status_filter: 'ALL', receipt_type: 'ALL', search: '',
    });
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);

    useEffect(() => {
        if (filters.wing_id) dispatch(getFlatsDropdown({ wing_id: filters.wing_id }));
    }, [dispatch, filters.wing_id]);

    const fetchReport = useCallback(() => {
        const params = { society_id: societyId, page, limit };
        if (filters.wing_id) params.wing_id = filters.wing_id;
        if (filters.flat_id) params.flat_id = filters.flat_id;
        if (filters.from_date) params.from_date = filters.from_date;
        if (filters.to_date) params.to_date = filters.to_date;
        if (filters.payment_mode !== 'ALL') params.payment_mode = filters.payment_mode;
        if (filters.status_filter !== 'ALL') params.status_filter = filters.status_filter;
        if (filters.receipt_type !== 'ALL') params.receipt_type = filters.receipt_type;
        if (filters.search) params.search = filters.search;
        dispatch(getReceiptReport(params));
    }, [dispatch, societyId, filters, page, limit]);
    usePageRefresh(fetchReport);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'wing_id') updated.flat_id = '';
            return updated;
        });
        setPage(1);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = { society_id: societyId };
            if (filters.wing_id) params.wing_id = filters.wing_id;
            if (filters.flat_id) params.flat_id = filters.flat_id;
            if (filters.from_date) params.from_date = filters.from_date;
            if (filters.to_date) params.to_date = filters.to_date;
            if (filters.payment_mode !== 'ALL') params.payment_mode = filters.payment_mode;
            if (filters.status_filter !== 'ALL') params.status_filter = filters.status_filter;
            if (filters.receipt_type !== 'ALL') params.receipt_type = filters.receipt_type;
            if (filters.search) params.search = filters.search;
            const response = await exportReceiptReportReq(params);
            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'receipt_report.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export failed:', e);
        } finally {
            setExporting(false);
        }
    };

    const handleReset = () => {
        setFilters({ wing_id: '', flat_id: '', from_date: '', to_date: '', payment_mode: 'ALL', status_filter: 'ALL', receipt_type: 'ALL', search: '' });
        setPage(1);
    };

    const receipts = receiptReportData || [];
    const summary = receiptReportSummary || {};
    const pagination = receiptReportPagination || {};
    const wings = wingsDropdown || [];
    const flats = Array.isArray(flatsDropdown) ? flatsDropdown : flatsDropdown?.data || [];

    const formatCurrency = (amount) => parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Receipt Report</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">View payment receipts and collections</p>
                </div>
                <button onClick={handleExport} disabled={exporting || receiptReportLoading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50">
                    {exporting ? <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" /> : <Icon icon="mdi:file-excel-outline" className="tw-text-sm" />}
                    {exporting ? 'Exporting...' : 'Export Excel'}
                </button>
            </div>

            {/* Summary Cards */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3 tw-mb-4">
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Receipts</div>
                    <div className="tw-text-xl tw-font-bold tw-text-gray-800 tw-mt-1">{summary.total_receipts || 0}</div>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Amount</div>
                    <div className="tw-text-xl tw-font-bold tw-text-emerald-700 tw-mt-1">{formatCurrency(summary.total_amount)}</div>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total TDS</div>
                    <div className="tw-text-xl tw-font-bold tw-text-orange-600 tw-mt-1">{formatCurrency(summary.total_tds)}</div>
                </div>
            </div>

            {/* Filters */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 lg:tw-grid-cols-8 tw-gap-3">
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Wing</label>
                        <select value={filters.wing_id} onChange={(e) => handleFilterChange('wing_id', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            <option value="">All Wings</option>
                            {wings.map(w => <option key={w.wing_id} value={w.wing_id}>{w.title}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Flat</label>
                        <select value={filters.flat_id} onChange={(e) => handleFilterChange('flat_id', e.target.value)} disabled={!filters.wing_id}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400">
                            <option value="">All Flats</option>
                            {flats.map(f => <option key={f.flat_id} value={f.flat_id}>{f.flat_number || f.flat_name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">From Date</label>
                        <input type="date" value={filters.from_date} onChange={(e) => handleFilterChange('from_date', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">To Date</label>
                        <input type="date" value={filters.to_date} onChange={(e) => handleFilterChange('to_date', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Payment Mode</label>
                        <select value={filters.payment_mode} onChange={(e) => handleFilterChange('payment_mode', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            {PAYMENT_MODES.map(m => <option key={m} value={m}>{m === 'ALL' ? 'All Modes' : m}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Status</label>
                        <select value={filters.status_filter} onChange={(e) => handleFilterChange('status_filter', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Type</label>
                        <select value={filters.receipt_type} onChange={(e) => handleFilterChange('receipt_type', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Search</label>
                        <div className="tw-relative">
                            <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                            <input type="text" placeholder="Receipt or ref no..." value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                        </div>
                    </div>
                </div>
                <div className="tw-flex tw-justify-end tw-mt-3">
                    <button onClick={handleReset} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">
                        <Icon icon="mdi:refresh" className="tw-text-sm" /> Reset
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                {['Sr No', 'Receipt No', 'Date', 'Type', 'Wing', 'Unit', 'Member Name', 'Invoice No(s)', 'Pay Mode', 'Reference No', 'Bank', 'Amount', 'TDS', 'Status'].map((h, i) => (
                                    <th key={i} className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap"
                                        style={{ textAlign: [0, 2, 13].includes(i) ? 'center' : [11, 12].includes(i) ? 'right' : 'left' }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {receiptReportLoading ? (
                                <tr><td colSpan={14} className="tw-text-center tw-py-12">
                                    <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" />
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-2">Loading report...</p>
                                </td></tr>
                            ) : receipts.length === 0 ? (
                                <tr><td colSpan={14} className="tw-text-center tw-py-12">
                                    <Icon icon="mdi:file-document-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No receipts found</p>
                                </td></tr>
                            ) : receipts.map((r, idx) => (
                                <tr key={r.receipt_id} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/50">
                                    <td className="tw-px-3 tw-py-2 tw-text-center tw-text-gray-500">{(page - 1) * limit + idx + 1}</td>
                                    <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700 tw-font-medium">{r.receipt_no}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-center tw-whitespace-nowrap tw-text-gray-700">{formatDate(r.receipt_date)}</td>
                                    <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                                        <span className={`tw-inline-flex tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${r.receipt_type === 'ADVANCE' ? 'tw-bg-purple-50 tw-text-purple-700' : 'tw-bg-blue-50 tw-text-blue-700'}`}>
                                            {r.receipt_type}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{r.wing}</td>
                                    <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{r.unit_no}</td>
                                    <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{r.member_name}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-max-w-[180px] tw-truncate" title={r.invoice_nos}>{r.invoice_nos || '-'}</td>
                                    <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">{r.payment_mode}</td>
                                    <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">{r.reference_no || '-'}</td>
                                    <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">{r.bank_name || '-'}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-font-medium tw-text-gray-800">{formatCurrency(r.amount)}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-text-gray-600">{formatCurrency(r.tds_deducted)}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-center tw-whitespace-nowrap">
                                        <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${STATUS_STYLES[r.status] || 'tw-bg-gray-50 tw-text-gray-600'}`}>
                                            {r.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {pagination.total > 0 && (
                <PaginationLimit
                    currentPage={page}
                    totalRecords={pagination.total || 0}
                    limit={limit}
                    handleLimitChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(p) => setPage(p)}
                />
            )}
        </>
    );
};

export default ReceiptReport;
